import PropTypes from 'prop-types';
import { get, set } from 'lodash';
import React, { useState, useEffect } from 'react';
import { Form, Grid, Row, Col } from 'react-bootstrap/lib';
import { getDealerFlow } from '@upstart/harmony/lib/flow-utils';
import {
  CLIENT_TYPE,
  OnlineStepPath,
  OnlineStep,
  Condition,
} from '@upstart/harmony/lib/constants';
import Switch from '../inputs/Switch';
import TextInput from '../inputs/TextInput';
import NumberInput from '../inputs/NumberInput';
import ColorPicker from '../inputs/ColorPicker';
import CodeTextArea from '../inputs/CodeTextArea';
import ImageUpload from '../../../WidgetConfigEditor/inputs/ImageUpload';
import Select from '../inputs/Select';
import {
  widgetsScriptURL,
  ddcWidgetsAdapterURL,
} from '../../../../util/constants';

function PricingButtonGroupEditor(props) {
  const {
    onSave,
    onChange,
    website,
    formData,
    page: { type },
  } = props;

  // Websites will have a dealer who can store flags. For DealerGroups,
  // their configuration is still stored in a dealer object and thus this logic still works perfectly fine.
  const cpoEnabled = website?.Dealer?.flags?.useCPO;

  const opts = getDealerFlow(website)
    [CLIENT_TYPE.ONLINE].filter(
      step =>
        // Filter out invalid steps
        // (not valid target pages for CTA widget button)
        [OnlineStep.upgradePackages, OnlineStep.schedulePickup].indexOf(
          step.id,
        ) === -1,
    )
    .map(activeStep => ({
      id: OnlineStepPath[activeStep.id],
      name: activeStep.name,
    }));

  opts.unshift({
    id: 'start',
    name: 'Vehicle Detail',
  });
  const index = opts.findIndex(o => o.id === 'payment-builder');
  opts.splice(index, 1);
  // Use Payment page as first option
  opts.unshift({ id: 'payment-builder', name: 'Customize Your Payments' });

  const onChangeHandler = e => {
    set(formData, e.target.name, e.target.value);
    onChange(formData);
  };

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    onSave();
  };

  // We cannot turn on widget rebates until APR purchase rebates are enabled
  const areIncentivesDisabled = () => {
    const rebateStatus = get(
      website,
      'aprConfig.options.useManufacturerRebate.purchase',
    );
    // If rebates are off, this cannot go on until those come on.
    if (rebateStatus === false) {
      return true;
    }
    return false;
  };

  const getPricingStack = condition => (
    <>
      <div className="widget-text-toggle-group">
        <Switch
          name={`${condition}.msrp.enabled`}
          label="MSRP"
          formData={formData}
          onChange={onChangeHandler}
        />
        <div>
          <Switch
            name={`${condition}.msrp.visible`}
            label="Price Visibility"
            formData={formData}
            onChange={onChangeHandler}
            disabled={!get(formData, `${condition}.msrp.enabled`)}
          />
        </div>
        <div>
          <Switch
            name={`${condition}.discounts.enabled`}
            label="Dealer Discount"
            formData={formData}
            onChange={onChangeHandler}
          />
        </div>
        <div>
          <TextInput
            name={`${condition}.discounts.text`}
            label="Dealer Discount Label"
            formData={formData}
            onChange={onChangeHandler}
            placeholder="Discount"
            disabled={!get(formData, `${condition}.discounts.enabled`)}
          />
        </div>
        {areIncentivesDisabled() && (
          <span className="widget-warning">
            &quot;Incentives & Rebates&quot; is disabled until Pricing &gt;&gt;
            Online APR Configuration &gt;&gt; Manufacturer Rebates &gt;&gt;
            Purchase is Enabled
          </span>
        )}
        <div>
          <Switch
            name={`${condition}.marketAdjustments.enabled`}
            label="Dealer Discount and Market Adjustment"
            formData={formData}
            onChange={onChangeHandler}
            disabled={areIncentivesDisabled()}
          />
        </div>
        <div>
          <Switch
            name={`${condition}.incentives.enabled`}
            label="Incentives & Rebates"
            formData={formData}
            onChange={onChangeHandler}
            disabled={areIncentivesDisabled()}
          />
        </div>
      </div>

      <div className="widget-text-toggle-group">
        <Switch
          name={`${condition}.bestPrice.enabled`}
          label="Best Price"
          formData={formData}
          onChange={onChangeHandler}
        />
        {get(formData, `${condition}.bestPrice.enabled`) && (
          <div>
            <TextInput
              name={`${condition}.bestPrice.text`}
              label="Best Price Text"
              formData={formData}
              onChange={onChangeHandler}
              placeholder="Our Price"
            />
            <Switch
              name={`${condition}.bestPrice.visible`}
              label="Price Visibility"
              formData={formData}
              onChange={onChangeHandler}
            />
          </div>
        )}
      </div>
      <div className="widget-text-toggle-group">
        <Switch
          name={`${condition}.lowestPrice.enabled`}
          label="Lowest Price"
          formData={formData}
          onChange={onChangeHandler}
        />
        {get(formData, `${condition}.lowestPrice.enabled`) && (
          <div>
            <TextInput
              name={`${condition}.lowestPrice.text`}
              label="Lowest Price Text"
              formData={formData}
              onChange={onChangeHandler}
              placeholder="Lowest Price"
            />
            <TextInput
              name={`${condition}.lowestPrice.disclaimer`}
              label="Lowest Price Disclaimer"
              formData={formData}
              onChange={onChangeHandler}
              maxLength="300"
              placeholder="Includes incentives available to all consumers and, in some cases, conditional incentives available to most consumers. In addition, other incentives may be available. Incentives are offered on a limited time basis. Please see dealer for full details."
            />
          </div>
        )}
      </div>
      <div className="widget-text-toggle-group">
        <Switch
          name={`${condition}.enableDisclaimerText`}
          label="Enable Disclaimer Text"
          formData={formData}
          onChange={onChangeHandler}
        />
        {get(formData, `${condition}.enableDisclaimerText`) && (
          <TextInput
            name={`${condition}.disclaimerText`}
            label="Disclaimer Text"
            formData={formData}
            onChange={onChangeHandler}
            placeholder="*Includes OEM incentives and rebates"
            required={false}
          />
        )}
      </div>
    </>
  );

  const defaultButtonsGroupOrder = [
    {
      name: 'TradeIn',
      buttonType: 'tradeInButton',
      placeholder: 'Value Your Trade',
    },
    {
      name: 'Payment Options',
      buttonType: 'paymentOptionsButton',
      placeholder: 'Customize Your Payment Options',
    },
    {
      name: 'Pre-Approved',
      buttonType: 'preApprovalButton',
      placeholder: 'Get Pre-Approved',
    },
    {
      name: 'Test Drive',
      buttonType: 'testDriveButton',
      placeholder: 'Schedule Your Test Drive',
    },
  ];

  const [buttonsGroup, setButtonsGroup] = useState([]);

  useEffect(() => {
    const buttons = [];
    defaultButtonsGroupOrder.forEach((el, idx) => {
      const buttonOrder = get(formData, `${el.buttonType}.order`);
      if (typeof buttonOrder === 'number') {
        buttons[buttonOrder] = el;
      } else {
        buttons.push(el);
        set(formData, `${el.buttonType}.order`, idx);
      }
    });
    setButtonsGroup(buttons.filter(el => Boolean(el)));
  }, []);

  // eslint-disable-next-line react/prop-types
  const renderActionButtonSection = ({ name, buttonType, placeholder }) => {
    const buttonEnabled = get(formData, `${buttonType}.enabled`);
    const buttonOrder = get(formData, `${buttonType}.order`);
    return (
      <div className="widget-text-toggle-group">
        <div className="head">
          <Switch
            name={`${buttonType}.enabled`}
            label={`${name} Button`}
            formData={formData}
            onChange={onChangeHandler}
          />
          <div className="position-buttons">
            <button
              type="button"
              className="btn btn-up"
              disabled={buttonOrder === 0}
              onClick={() => handleSwap(buttonOrder, 'up')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="1em"
                viewBox="0 0 384 512"
              >
                <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2V448c0 17.7 14.3 32 32 32s32-14.3 32-32V141.2L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
              </svg>
            </button>
            <button
              type="button"
              className="btn btn-down"
              disabled={buttonOrder === defaultButtonsGroupOrder.length - 1}
              onClick={() => handleSwap(buttonOrder, 'down')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="1em"
                viewBox="0 0 384 512"
              >
                <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
              </svg>
            </button>
          </div>
        </div>

        {buttonEnabled && (
          <>
            <TextInput
              name={`${buttonType}.text`}
              label={`${name} Button Text`}
              formData={formData}
              onChange={onChangeHandler}
              placeholder={placeholder}
            />
            {get(formData, 'useCTAImages') && (
              <ImageUpload
                name={`${buttonType}.image`}
                label={`${name} Image Upload`}
                formData={formData}
                onChange={onChangeHandler}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const leadButtonDefaultText = get(formData, 'leadCaptureButton.defaultText');
  const leadButtonDealDefaultText = get(
    formData,
    'leadCaptureButton.defaultDealText',
  );

  const changeOrder = (arr, a, b) => {
    const tmp = [...arr];
    tmp[a] = tmp.splice(b, 1, tmp[a])[0];
    return tmp;
  };

  const handleSwap = (order, direction) => {
    let result;
    if (direction === 'up' && order > 0) {
      result = changeOrder(buttonsGroup, order, order - 1);
    }
    if (direction === 'down' && order < buttonsGroup.length - 1) {
      result = changeOrder(buttonsGroup, order, order + 1);
    }
    if (result) {
      result.forEach((el, idx) => {
        set(formData, `${el.buttonType}.order`, idx);
      });
      setTimeout(() => {
        setButtonsGroup(result);
        onChange(formData);
      }, 500);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid fluid>
        <Row>
          <Col sm={4}>
            <h4>Pricing Stack - New Vehicles</h4>
            {getPricingStack(Condition.NEW)}
          </Col>
          <Col sm={4}>
            <h4>Pricing Stack - Used Vehicles</h4>
            {getPricingStack(Condition.USED)}
          </Col>
          {!!cpoEnabled && (
            <Col sm={4}>
              <h4>Pricing Stack - CPO Vehicles</h4>
              {getPricingStack(Condition.CPO)}
            </Col>
          )}
        </Row>
        <Row>
          <Col sm={6}>
            <div className="widget-text-toggle-group">
              <Switch
                name="useCTAImages"
                label="CTA Images Instead of Text"
                formData={formData}
                onChange={onChangeHandler}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={5}>
            <h4>Lead Capture Button</h4>
            <div className="widget-text-toggle-group">
              <Switch
                name="leadCaptureButton.enabled"
                label="Lead Capture Button"
                formData={formData}
                onChange={onChangeHandler}
              />
            </div>
            {!!get(formData, `leadCaptureButton.enabled`) && (
              <>
                <Select
                  label="Target Page"
                  name="leadCaptureButton.target"
                  value={formData.leadCaptureButton.target}
                  formData={formData}
                  onChange={onChangeHandler}
                  info="Page that user will land on after clicking this button"
                >
                  {opts.map(s => (
                    <Select.Option
                      name={s.name}
                      value={s.id}
                      key={`option-key-${s.id}`}
                    />
                  ))}
                </Select>
                {get(formData, 'useCTAImages') && (
                  <>
                    <ImageUpload
                      name="leadCaptureButton.image"
                      label="Button Image - New Vehicle"
                      formData={formData}
                      onChange={onChangeHandler}
                    />
                    <ImageUpload
                      name="leadCaptureButton.dealImage"
                      label="Button Image - New Vehicle With Deal"
                      formData={formData}
                      onChange={onChangeHandler}
                    />
                    <ImageUpload
                      name="leadCaptureButton.imageUsed"
                      label="Button Image - Used Vehicle"
                      formData={formData}
                      onChange={onChangeHandler}
                    />
                    <ImageUpload
                      name="leadCaptureButton.dealImageUsed"
                      label="Button Image - Used Vehicle With Deal"
                      formData={formData}
                      onChange={onChangeHandler}
                    />
                    {!!cpoEnabled && (
                      <>
                        <ImageUpload
                          name="leadCaptureButton.imageCPO"
                          label="Button Image - CPO Vehicle"
                          formData={formData}
                          onChange={onChangeHandler}
                        />
                        <ImageUpload
                          name="leadCaptureButton.dealImageCPO"
                          label="Button Image - CPO Vehicle With Deal"
                          formData={formData}
                          onChange={onChangeHandler}
                        />
                      </>
                    )}
                    <ImageUpload
                      name="leadCaptureButton.defaultImage"
                      label="Button Image - Default (Optional Fallback)"
                      formData={formData}
                      onChange={onChangeHandler}
                    />
                  </>
                )}
                <TextInput
                  name="leadCaptureButton.text"
                  label="Button Text - New Vehicle"
                  formData={formData}
                  onChange={onChangeHandler}
                  placeholder={leadButtonDefaultText}
                />
                <TextInput
                  name="leadCaptureButton.dealText"
                  label="Button Text - New Vehicle with Deal"
                  formData={formData}
                  onChange={onChangeHandler}
                  placeholder={leadButtonDealDefaultText}
                />
                <TextInput
                  name="leadCaptureButton.textUsed"
                  label="Button Text - Used Vehicle"
                  formData={formData}
                  onChange={onChangeHandler}
                  placeholder={leadButtonDefaultText}
                />
                <TextInput
                  name="leadCaptureButton.dealTextUsed"
                  label="Button Text - Used Vehicle with Deal"
                  formData={formData}
                  onChange={onChangeHandler}
                  placeholder={leadButtonDealDefaultText}
                />
                {!!cpoEnabled && (
                  <>
                    <TextInput
                      name="leadCaptureButton.textCPO"
                      label="Button Text - CPO Vehicle"
                      formData={formData}
                      onChange={onChangeHandler}
                      placeholder={leadButtonDefaultText}
                    />
                    <TextInput
                      name="leadCaptureButton.dealTextCPO"
                      label="Button Text - CPO Vehicle with Deal"
                      formData={formData}
                      onChange={onChangeHandler}
                      placeholder={leadButtonDealDefaultText}
                    />
                  </>
                )}
                <TextInput
                  name="leadCaptureButton.dealTextNotInInventory"
                  label="Button Text - Vehicle not in inventory"
                  formData={formData}
                  onChange={onChangeHandler}
                  placeholder={'Get Details'}
                />
                <NumberInput
                  name="leadCaptureButton.textSize"
                  label="Button Text Size"
                  formData={formData}
                  onChange={onChangeHandler}
                  info="Enter value in pixels"
                />
                <NumberInput
                  name="leadCaptureButton.radius"
                  label="Button Radius"
                  formData={formData}
                  onChange={onChangeHandler}
                  info="Enter value in pixels"
                  min={0}
                  max={25}
                  isRequired={false}
                />
                <ColorPicker
                  name="leadCaptureButton.backgroundColor"
                  label="Button Background Color"
                  formData={formData}
                  onChange={onChangeHandler}
                />
                <ColorPicker
                  name="leadCaptureButton.textColor"
                  label="Button Text Color"
                  formData={formData}
                  onChange={onChangeHandler}
                />
              </>
            )}
          </Col>
          <Col sm={5} smOffset={1}>
            <h4>Action Buttons</h4>
            <NumberInput
              name="actionTextSize"
              label="Button Text Size"
              formData={formData}
              onChange={onChangeHandler}
              info="Enter value in pixels"
            />
            <NumberInput
              name="actionRadius"
              label="Button Radius"
              formData={formData}
              onChange={onChangeHandler}
              info="Enter value in pixels"
              min={0}
              max={25}
              isRequired={false}
            />
            <ColorPicker
              name="actionBackgroundColor"
              label="Button Background Color"
              formData={formData}
              onChange={onChangeHandler}
            />
            <ColorPicker
              name="actionTextColor"
              label="Button Text Color"
              formData={formData}
              onChange={onChangeHandler}
            />
            <NumberInput
              name="actionButtonMargin"
              label="Button Margin"
              formData={formData}
              onChange={onChangeHandler}
              info="Enter value in pixels"
              min={10}
              max={50}
              isRequired={false}
            />
            {buttonsGroup.map(el => renderActionButtonSection(el))}
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <h4>Standard Integration</h4>
            <small>
              Copy the markup below into your page template to render the Button
              Widget. Only add this script once per page.
            </small>
            <CodeTextArea
              label="Agent Script"
              value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${widgetsScriptURL}" type="text/javascript" id="prodigyButtonGroupAgent" websiteId="${website.id}"></script>`}
            />
            <CodeTextArea
              label="Button Container"
              value={`<!— Copy this markup into your page template where you want the button group to render —>\n<div class="prodigy-pricing-button-group" data-vin="" data-config-type="${type}"></div>`}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <h4>Dealer Dot Com Integration</h4>
            <small>Provide the following script to DDC support.</small>
            <CodeTextArea
              label="DDC Adapter Script"
              value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${ddcWidgetsAdapterURL}" type="text/javascript></script>`}
            />
            <small>
              Additionally provide the following websiteId to DDC support.
            </small>
            <CodeTextArea
              label="Website Id"
              value={`websiteId = ${website.id}`}
            />
          </Col>
        </Row>
      </Grid>
    </Form>
  );
}

PricingButtonGroupEditor.propTypes = {
  website: PropTypes.object,
  page: PropTypes.object,
  formData: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
};

export default PricingButtonGroupEditor;
